import React, { useState, useEffect } from 'react';
import './MenuManagement.css';
import { storeService } from '../services/storeService';

interface MenuManagementProps {
  storeData: any;
}

const MenuManagement: React.FC<MenuManagementProps> = ({ storeData }) => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [nameSuggestions, setNameSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [variants, setVariants] = useState<any[]>([{ quantity: '1', unit: 'kg', price: '' }]);
  const [formData, setFormData] = useState({
    name: '',
    nameInTelugu: '',
    price: '',
    category: '',
    image: '',
    available: true
  });

  // Fetch menu items on component mount
  useEffect(() => {
    if (storeData?._id) {
      fetchMenuItems();
    }
  }, [storeData?._id]);

  const fetchMenuItems = async () => {
    try {
      setLoading(true);
      const response = await storeService.getStoreMenu(storeData._id);
      setItems(response.menuItems || []);
    } catch (error) {
      console.error('Error fetching menu items:', error);
    } finally {
      setLoading(false);
    }
  };

  // Category-specific suggested items
  const getCategorySuggestions = (category: string) => {
    const suggestions: any = {
      grocery: [
        { name: 'Rice', nameTelugu: 'బియ్యం', icon: '🍚', defaultPrice: 60 },
        { name: 'Dal', nameTelugu: 'పప్పు', icon: '🥘', defaultPrice: 120 },
        { name: 'Oil', nameTelugu: 'నూనే', icon: '🫒', defaultPrice: 180 },
        { name: 'Sugar', nameTelugu: 'చక్కెర', icon: '🧂', defaultPrice: 45 },
        { name: 'Salt', nameTelugu: 'ఉప్పు', icon: '🧂', defaultPrice: 20 },
        { name: 'Wheat Flour', nameTelugu: 'గోధుమ పిండి', icon: '🌾', defaultPrice: 50 }
      ],
      pharmacy: [
        { name: 'Paracetamol', nameTelugu: 'పారాసిటమాల్', icon: '💊', defaultPrice: 10 },
        { name: 'Vitamin C', nameTelugu: 'విటమిన్ సి', icon: '💊', defaultPrice: 150 },
        { name: 'Hand Sanitizer', nameTelugu: 'శానిటైజర్', icon: '🧴', defaultPrice: 50 },
        { name: 'Face Mask', nameTelugu: 'మాస్క్', icon: '😷', defaultPrice: 5 },
        { name: 'First Aid Kit', nameTelugu: 'ప్రథమ చికిత్స', icon: '🩹', defaultPrice: 200 }
      ],
      vegetables: [
        { name: 'Tomato', nameTelugu: 'టమాటో', icon: '🍅', defaultPrice: 40 },
        { name: 'Onion', nameTelugu: 'ఉల్లిపాయ', icon: '🧅', defaultPrice: 35 },
        { name: 'Potato', nameTelugu: 'బంగాళాదుంప', icon: '🥔', defaultPrice: 30 },
        { name: 'Carrot', nameTelugu: 'క్యారెట్', icon: '🥕', defaultPrice: 50 },
        { name: 'Leafy Greens', nameTelugu: 'ఆకుకూరలు', icon: '🥬', defaultPrice: 20 }
      ],
      meat: [
        { name: 'Chicken', nameTelugu: 'కోడి', icon: '🍗', defaultPrice: 200 },
        { name: 'Mutton', nameTelugu: 'మటన్', icon: '🥩', defaultPrice: 500 },
        { name: 'Fish', nameTelugu: 'చేప', icon: '🐟', defaultPrice: 250 },
        { name: 'Prawns', nameTelugu: 'రొయ్యలు', icon: '🦐', defaultPrice: 400 },
        { name: 'Eggs', nameTelugu: 'గుడ్లు', icon: '🥚', defaultPrice: 60 }
      ],
      bakery: [
        { name: 'White Bread', nameTelugu: 'బ్రెడ్', icon: '🍞', defaultPrice: 35 },
        { name: 'Cake', nameTelugu: 'కేక్', icon: '🎂', defaultPrice: 250 },
        { name: 'Cookies', nameTelugu: 'బిస్కట్లు', icon: '🍪', defaultPrice: 50 },
        { name: 'Pastry', nameTelugu: 'పేస్ట్రీ', icon: '🥐', defaultPrice: 40 },
        { name: 'Buns', nameTelugu: 'బన్స్', icon: '🥖', defaultPrice: 30 }
      ],
      dairy: [
        { name: 'Milk', nameTelugu: 'పాలు', icon: '🥛', defaultPrice: 60 },
        { name: 'Curd', nameTelugu: 'పెరుగు', icon: '🥛', defaultPrice: 40 },
        { name: 'Butter', nameTelugu: 'వెన్న', icon: '🧈', defaultPrice: 80 },
        { name: 'Cheese', nameTelugu: 'చీజ్', icon: '🧀', defaultPrice: 120 },
        { name: 'Paneer', nameTelugu: 'పన్నీర్', icon: '🧀', defaultPrice: 100 }
      ],
      restaurant: [
        { name: 'Biryani', nameTelugu: 'బిర్యానీ', icon: '🍛', defaultPrice: 180 },
        { name: 'Curry', nameTelugu: 'కూర', icon: '🍲', defaultPrice: 120 },
        { name: 'Roti', nameTelugu: 'రోటీ', icon: '🫓', defaultPrice: 15 },
        { name: 'Fried Rice', nameTelugu: 'ఫ్రైడ్ రైస్', icon: '🍚', defaultPrice: 140 },
        { name: 'Starters', nameTelugu: 'స్టార్టర్స్', icon: '🍢', defaultPrice: 100 }
      ],
      sweets: [
        { name: 'Laddu', nameTelugu: 'లడ్డు', icon: '🍬', defaultPrice: 200 },
        { name: 'Jalebi', nameTelugu: 'జిలేబి', icon: '🥨', defaultPrice: 150 },
        { name: 'Barfi', nameTelugu: 'బర్ఫీ', icon: '🍬', defaultPrice: 250 },
        { name: 'Halwa', nameTelugu: 'హల్వా', icon: '🍮', defaultPrice: 180 },
        { name: 'Mysore Pak', nameTelugu: 'మైసూర్ పాక్', icon: '🍰', defaultPrice: 220 }
      ],
      beverages: [
        { name: 'Soft Drink', nameTelugu: 'శీతల పానీయం', icon: '🥤', defaultPrice: 40 },
        { name: 'Juice', nameTelugu: 'జ్యూస్', icon: '🧃', defaultPrice: 60 },
        { name: 'Water', nameTelugu: 'నీరు', icon: '💧', defaultPrice: 20 },
        { name: 'Energy Drink', nameTelugu: 'ఎనర్జీ డ్రింక్', icon: '🥤', defaultPrice: 80 },
        { name: 'Tea/Coffee', nameTelugu: 'టీ/కాఫీ', icon: '☕', defaultPrice: 15 }
      ]
    };
    return suggestions[category] || [];
  };

  const categoryInfo = getCategorySuggestions(storeData.category || 'general');

  // Get product name suggestions based on category
  const getProductNameSuggestions = () => {
    const allProducts: any = {
      grocery: ['Rice', 'Dal', 'Oil', 'Sugar', 'Salt', 'Wheat Flour', 'Atta', 'Maida', 'Rava', 'Poha', 'Cornflakes', 'Oats', 'Milk Powder', 'Tea', 'Coffee', 'Biscuits', 'Noodles', 'Pasta'],
      vegetables: ['Tomato', 'Onion', 'Potato', 'Carrot', 'Beans', 'Cabbage', 'Cauliflower', 'Brinjal', 'Capsicum', 'Cucumber', 'Leafy Greens', 'Coriander', 'Mint', 'Curry Leaves'],
      meat: ['Chicken', 'Mutton', 'Fish', 'Prawns', 'Eggs', 'Chicken Breast', 'Chicken Legs', 'Mutton Chops', 'Mutton Keema', 'Pomfret', 'Rohu', 'Prawns Medium', 'Prawns Large'],
      dairy: ['Milk', 'Curd', 'Butter', 'Cheese', 'Paneer', 'Ghee', 'Buttermilk', 'Cream', 'Ice Cream'],
      bakery: ['Bread', 'Cake', 'Cookies', 'Pastry', 'Buns', 'Croissant', 'Donuts', 'Muffins', 'Brownies'],
      pharmacy: ['Paracetamol', 'Vitamin C', 'Sanitizer', 'Masks', 'First Aid', 'Bandages', 'Dettol', 'Vicks', 'Cough Syrup'],
      restaurant: ['Biryani', 'Curry', 'Roti', 'Naan', 'Fried Rice', 'Manchuria', 'Starters', 'Tandoori', 'Kebab', 'Pulao'],
      sweets: ['Laddu', 'Jalebi', 'Barfi', 'Halwa', 'Mysore Pak', 'Gulab Jamun', 'Rasgulla', 'Peda', 'Kaju Katli'],
      beverages: ['Soft Drink', 'Juice', 'Water', 'Energy Drink', 'Tea', 'Coffee', 'Lassi', 'Milkshake', 'Smoothie'],
      general: ['Rice', 'Dal', 'Oil', 'Sugar', 'Bread', 'Milk', 'Tea', 'Coffee', 'Biscuits', 'Noodles']
    };
    
    // Get category-specific products or fallback to general
    const category = storeData?.category?.toLowerCase() || 'general';
    return allProducts[category] || allProducts['general'] || [];
  };

  const handleNameChange = (value: string) => {
    setFormData({ ...formData, name: value });
    if (value.length > 0) {
      const suggestions = getProductNameSuggestions().filter((product: string) =>
        product.toLowerCase().includes(value.toLowerCase())
      );
      setNameSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const selectSuggestion = (suggestion: string) => {
    setFormData({ ...formData, name: suggestion });
    setShowSuggestions(false);
  };

  const addVariant = () => {
    setVariants([...variants, { quantity: '', unit: 'kg', price: '' }]);
  };

  const removeVariant = (index: number) => {
    if (variants.length > 1) {
      setVariants(variants.filter((_, i) => i !== index));
    }
  };

  const updateVariant = (index: number, field: string, value: string) => {
    const newVariants = [...variants];
    newVariants[index][field] = value;
    setVariants(newVariants);
  };

  const handleAddItem = async (suggestion?: any) => {
    try {
      setLoading(true);
      
      if (suggestion) {
        // Quick add from suggestion
        const menuItem = {
          name: suggestion.name,
          nameInTelugu: suggestion.nameTelugu,
          icon: suggestion.icon,
          price: suggestion.defaultPrice,
          available: true
        };
        
        await storeService.addMenuItem(storeData._id, menuItem);
        alert(`✅ ${suggestion.name} added to your menu!`);
        await fetchMenuItems(); // Refresh list
      } else {
        // Custom add with variants
        if (!formData.name) {
          alert('Please fill in item name');
          return;
        }
        
        // Validate variants
        const validVariants = variants.filter(v => v.quantity && v.price);
        if (validVariants.length === 0) {
          alert('Please add at least one variant with quantity and price');
          return;
        }

        const menuItem = {
          name: formData.name,
          nameInTelugu: formData.nameInTelugu,
          image: formData.image,
          icon: formData.image ? undefined : '📦',
          category: formData.category,
          available: true,
          variants: validVariants.map(v => ({
            quantity: v.quantity,
            unit: v.unit,
            price: parseFloat(v.price)
          }))
        };
        
        await storeService.addMenuItem(storeData._id, menuItem);
        
        setFormData({
          name: '',
          nameInTelugu: '',
          price: '',
          category: '',
          image: '',
          available: true
        });
        setVariants([{ quantity: '1', unit: 'kg', price: '' }]);
        setImagePreview('');
        setShowAddForm(false);
        alert('✅ Item added successfully!');
        await fetchMenuItems(); // Refresh list
      }
    } catch (error: any) {
      console.error('Error adding item:', error);
      alert('Failed to add item: ' + (error.response?.data?.message || 'Please try again'));
    } finally {
      setLoading(false);
    }
  };

  const toggleAvailability = async (itemId: string) => {
    try {
      const item = items.find(i => i._id === itemId);
      if (!item) return;

      await storeService.updateMenuItem(storeData._id, itemId, {
        ...item,
        available: !item.available
      });
      
      await fetchMenuItems(); // Refresh list
    } catch (error) {
      console.error('Error toggling availability:', error);
      alert('Failed to update item availability');
    }
  };

  const deleteItem = async (itemId: string) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await storeService.deleteMenuItem(storeData._id, itemId);
        await fetchMenuItems(); // Refresh list
        alert('Item deleted successfully');
      } catch (error) {
        console.error('Error deleting item:', error);
        alert('Failed to delete item');
      }
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setImagePreview(base64String);
        setFormData({ ...formData, image: base64String });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImagePreview('');
    setFormData({ ...formData, image: '' });
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '25px',
        marginBottom: '20px'
      }}>
        <h1 style={{ 
          margin: '0 0 10px 0', 
          fontSize: '24px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          📋 Menu Management
        </h1>
        <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
          Add and manage items for your {storeData.category} store
        </p>
      </div>

      {/* Quick Add Suggestions */}
      {categoryInfo.length > 0 && (
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', color: '#333' }}>
            ⚡ Quick Add Items
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' }}>
            {categoryInfo.map((suggestion: any, idx: number) => (
              <button
                key={idx}
                onClick={() => handleAddItem(suggestion)}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '15px 10px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
                }}
              >
                <span style={{ fontSize: '32px' }}>{suggestion.icon}</span>
                <span>{suggestion.name}</span>
                <span style={{ fontSize: '11px', opacity: 0.9 }}>{suggestion.nameTelugu}</span>
                <span style={{ fontSize: '14px', fontWeight: '700' }}>₹{suggestion.defaultPrice}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Custom Add Button */}
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          style={{
            width: '100%',
            background: 'white',
            color: '#667eea',
            border: '2px dashed #667eea',
            borderRadius: '15px',
            padding: '15px',
            fontSize: '16px',
            fontWeight: '700',
            cursor: 'pointer'
          }}
        >
          {showAddForm ? '✕ Cancel' : '+ Add Custom Item'}
        </button>
      </div>

      {/* Custom Add Form */}
      {showAddForm && (
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '25px',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#333' }}>Add Custom Item</h3>
          <div style={{ display: 'grid', gap: '15px' }}>
            {/* Product Name with Suggestions */}
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Item Name * (Start typing for suggestions)"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                onFocus={() => formData.name && setShowSuggestions(nameSuggestions.length > 0)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '10px',
                  border: '1px solid #ddd',
                  fontSize: '15px',
                  boxSizing: 'border-box'
                }}
              />
              {showSuggestions && nameSuggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'white',
                  border: '2px solid #667eea',
                  borderRadius: '10px',
                  marginTop: '5px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 10000,
                  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)'
                }}>
                  <div style={{
                    padding: '8px 12px',
                    background: '#f8f9fa',
                    borderBottom: '1px solid #e0e0e0',
                    fontSize: '12px',
                    color: '#667eea',
                    fontWeight: '600'
                  }}>
                    💡 Suggestions ({nameSuggestions.length})
                  </div>
                  {nameSuggestions.map((suggestion, idx) => (
                    <div
                      key={idx}
                      onClick={() => selectSuggestion(suggestion)}
                      style={{
                        padding: '12px 15px',
                        cursor: 'pointer',
                        borderBottom: idx < nameSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f0f4ff';
                        e.currentTarget.style.color = '#667eea';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.color = '#333';
                      }}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <input
              type="text"
              placeholder="Name in Telugu"
              value={formData.nameInTelugu}
              onChange={(e) => setFormData({ ...formData, nameInTelugu: e.target.value })}
              style={{
                padding: '12px',
                borderRadius: '10px',
                border: '1px solid #ddd',
                fontSize: '15px'
              }}
            />

            {/* Variants Section */}
            <div style={{
              background: '#f8f9fa',
              padding: '15px',
              borderRadius: '10px',
              border: '1px solid #ddd'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h4 style={{ margin: 0, fontSize: '16px', color: '#333' }}>⚖️ Quantity & Price Variants</h4>
                <button
                  onClick={addVariant}
                  style={{
                    background: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  + Add Option
                </button>
              </div>

              {variants.map((variant, index) => (
                <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="Qty"
                    value={variant.quantity}
                    onChange={(e) => updateVariant(index, 'quantity', e.target.value)}
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                      fontSize: '14px'
                    }}
                  />
                  <select
                    value={variant.unit}
                    onChange={(e) => updateVariant(index, 'unit', e.target.value)}
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                      fontSize: '14px',
                      background: 'white'
                    }}
                  >
                    <option value="kg">kg</option>
                    <option value="gm">gm</option>
                    <option value="ltr">ltr</option>
                    <option value="ml">ml</option>
                    <option value="pcs">pcs</option>
                    <option value="pack">pack</option>
                    <option value="dozen">dozen</option>
                  </select>
                  <input
                    type="number"
                    placeholder="Price ₹"
                    value={variant.price}
                    onChange={(e) => updateVariant(index, 'price', e.target.value)}
                    style={{
                      flex: 1.5,
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                      fontSize: '14px'
                    }}
                  />
                  {variants.length > 1 && (
                    <button
                      onClick={() => removeVariant(index)}
                      style={{
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#666' }}>
                💡 Example: 1 kg ₹60, 500 gm ₹32, 250 gm ₹17
              </p>
            </div>
            
            {/* Image Upload Section */}
            <div style={{
              padding: '15px',
              background: '#f8f9fa',
              borderRadius: '10px',
              border: '2px dashed #667eea'
            }}>
              <label style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer'
              }}>
                <span style={{ fontSize: '32px' }}>📷</span>
                <span style={{ fontSize: '14px', color: '#667eea', fontWeight: '600' }}>
                  {imagePreview ? 'Change Product Photo' : 'Add Product Photo'}
                </span>
                <span style={{ fontSize: '12px', color: '#666' }}>
                  Max size: 5MB (JPG, PNG)
                </span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
              </label>
              
              {imagePreview && (
                <div style={{ marginTop: '15px', position: 'relative' }}>
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    style={{
                      width: '100%',
                      maxHeight: '200px',
                      objectFit: 'cover',
                      borderRadius: '10px'
                    }}
                  />
                  <button
                    onClick={removeImage}
                    style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => handleAddItem()}
              style={{
                padding: '15px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              Add Item
            </button>
          </div>
        </div>
      )}

      {/* Items List */}
      {items.length > 0 && (
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '25px'
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#333' }}>
            Your Menu Items ({items.length})
          </h3>
          <div style={{ display: 'grid', gap: '12px' }}>
            {items.map((item) => (
              <div
                key={item._id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px',
                  padding: '15px',
                  background: item.available ? '#f8f9fa' : '#ffe4e4',
                  borderRadius: '12px',
                  border: `2px solid ${item.available ? '#e0e0e0' : '#ffcccc'}`
                }}
              >
                {/* Product Image or Icon */}
                {item.image ? (
                  <img 
                    src={item.image} 
                    alt={item.name}
                    style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '10px',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <span style={{ fontSize: '32px' }}>{item.icon}</span>
                )}
                
                <div style={{ flex: 1 }}>
                  <p style={{ margin: '0 0 3px 0', fontSize: '16px', fontWeight: '700', color: '#333' }}>
                    {item.name}
                  </p>
                  {item.nameTelugu && (
                    <p style={{ margin: '0 0 3px 0', fontSize: '13px', color: '#666' }}>
                      {item.nameTelugu}
                    </p>
                  )}
                  
                  {/* Show variants if available */}
                  {item.variants ? (
                    <div style={{ marginTop: '5px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {item.variants.map((variant: any, idx: number) => (
                        <span
                          key={idx}
                          style={{
                            background: '#667eea',
                            color: 'white',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}
                        >
                          {variant.quantity} {variant.unit} - ₹{variant.price}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#667eea' }}>
                      ₹{item.price}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => toggleAvailability(item._id)}
                  style={{
                    padding: '8px 16px',
                    background: item.available ? '#22c55e' : '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {item.available ? '✓ Available' : '✕ Out'}
                </button>
                <button
                  onClick={() => deleteItem(item._id)}
                  style={{
                    padding: '8px 12px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {items.length === 0 && !showAddForm && (
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '40px',
          textAlign: 'center'
        }}>
          <span style={{ fontSize: '60px' }}>📦</span>
          <h3 style={{ margin: '15px 0 10px 0', color: '#666' }}>No items added yet</h3>
          <p style={{ margin: 0, color: '#999', fontSize: '14px' }}>
            Start by adding items from quick suggestions above
          </p>
        </div>
      )}
    </div>
  );
};

export default MenuManagement;
